import { X } from "lucide-react";
import { useState } from "react";
import { useCanAccess, useTranslate } from "ra-core";

import type { Tag } from "../types";
import { TagEditModal } from "./TagEditModal";

type TagChipProps = {
  tag: Tag;

  // Almikia Epic E3b (TASK-065): omitted on a team member's contact -- a
  // lider cannot unlink the tag there (RLS rejects the `contacts` write),
  // so no remove control is offered.
  onUnlink?: () => Promise<void>;
};

export function TagChip({ tag, onUnlink }: TagChipProps) {
  const [open, setOpen] = useState(false);
  const translate = useTranslate();
  // Renaming a tag is a `tags` write (direccion only); other roles get a plain chip.
  const { canAccess: canEditTag } = useCanAccess({
    resource: "tags",
    action: "edit",
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = () => {
    if (canEditTag) setOpen(true);
  };

  return (
    <>
      <div
        className="text-black inline-flex items-center gap-1 px-4 md:px-2 py-2 md:py-1 text-sm md:text-xs rounded-md cursor-pointer hover:opacity-80 transition-opacity"
        style={{ backgroundColor: tag.color }}
        onClick={handleClick}
      >
        {tag.name}
        {onUnlink && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onUnlink();
            }}
            aria-label={translate("ra.action.remove")}
            className="transition-colors -m-3.5 md:m-0 p-3.5 md:p-0 ml-1 cursor-pointer inline-flex items-center justify-center"
          >
            <X aria-hidden="true" className="w-4 h-4 md:w-3 md:h-3" />
          </button>
        )}
      </div>
      {canEditTag && (
        <TagEditModal tag={tag} open={open} onClose={handleClose} />
      )}
    </>
  );
}
